import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { showToast } from '../utils/toast';
import { sanitizeInput, sanitizeHTML } from '../utils/sanitize';
import { API_BASE_URL } from '../config/api';
import GymCalendar from './GymCalendar';
import LoadingSpinner from './LoadingSpinner';
import EmptyState from './EmptyState';
import { ReportSkeleton } from './LoadingSkeleton';
import imageCompression from 'browser-image-compression';
import './Izvestaji.css';

function Izvestaji() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [expandedReport, setExpandedReport] = useState(null);
  const [reportDetails, setReportDetails] = useState({}); // Store report details with comments
  const [showForm, setShowForm] = useState(false);
  const [newReport, setNewReport] = useState({ report_date: '', content: '' });
  const [reportImages, setReportImages] = useState([]); // Store uploaded image URLs for new report
  const [uploadingImages, setUploadingImages] = useState(false);
  const [commentTexts, setCommentTexts] = useState({}); // Store comment text per report
  const [loading, setLoading] = useState(true);
  const [editingReportId, setEditingReportId] = useState(null);
  const [editForm, setEditForm] = useState({ report_date: '', content: '' });
  const [editReportImages, setEditReportImages] = useState([]); // Store images for editing
  const [selectedImage, setSelectedImage] = useState(null); // For modal view
  const [hasReportForDate, setHasReportForDate] = useState(false); // Track if selected date has a report

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'coach';
  const targetUserId = isAdmin && selectedCustomer ? selectedCustomer.id : user?.id;

  // Update hasReportForDate when reports or selected date changes
  useEffect(() => {
    if (newReport.report_date && reports.length > 0) {
      const dateStr = newReport.report_date;
      const hasReport = reports.some(r => {
        const reportDate = r.report_date ? r.report_date.split('T')[0] : r.report_date;
        return reportDate === dateStr;
      });
      setHasReportForDate(hasReport);
    } else {
      setHasReportForDate(false);
    }
  }, [newReport.report_date, reports]);

  useEffect(() => {
    if (isAdmin) {
      fetchCustomers();
      fetchReports();
    } else {
      fetchReports();
    }
  }, [user, isAdmin, selectedCustomer]);

  // Fetch report details when expanded
  useEffect(() => {
    if (expandedReport && !reportDetails[expandedReport]) {
      fetchReportDetails(expandedReport);
    }
  }, [expandedReport]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users`);
      const customerList = response.data.filter(u => u.role === 'customer');
      setCustomers(customerList);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchReports = async () => {
    try {
      setLoading(true);
      const url = isAdmin && selectedCustomer
        ? `${API_BASE_URL}/api/reports?userId=${selectedCustomer.id}`
        : `${API_BASE_URL}/api/reports`;
      
      const response = await axios.get(url);
      const reportsData = Array.isArray(response.data) ? response.data : [];
      console.log('Izvestaji - Fetched reports:', reportsData.length, 'reports');
      console.log('Izvestaji - Report dates:', reportsData.map(r => ({ id: r.id, date: r.report_date })));
      setReports(reportsData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setReports([]); // Set empty array on error
      setLoading(false);
    }
  };

  const fetchReportDetails = async (reportId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/reports/${reportId}`);
      setReportDetails(prev => ({ ...prev, [reportId]: response.data }));
      return response.data;
    } catch (error) {
      console.error('Error fetching report details:', error);
      return null;
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return null;

    // Validate file type first
    if (!file.type.startsWith('image/')) {
      showToast.error('Please upload an image file');
      return null;
    }

    // Validate original file size (10MB limit before compression)
    if (file.size > 10 * 1024 * 1024) {
      showToast.error('Image size must be less than 10MB');
      return null;
    }

    try {
      // Compress image before uploading to save Cloudinary bandwidth/storage
      const compressionOptions = {
        maxSizeMB: 1, // Target 1MB after compression
        maxWidthOrHeight: 1920, // Max dimension (good quality for reports)
        useWebWorker: true, // Use web worker for better performance
        fileType: file.type, // Preserve original file type
        initialQuality: 0.8, // 80% quality (good balance)
      };

      // Show compression status
      const compressedFile = await imageCompression(file, compressionOptions);
      
      // Log compression stats (for debugging - can remove in production)
      const compressionRatio = ((1 - compressedFile.size / file.size) * 100).toFixed(1);
      if (compressedFile.size < file.size) {
        console.log(`Image compressed: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB (${compressionRatio}% reduction)`);
      }

      const formData = new FormData();
      formData.append('image', compressedFile, compressedFile.name);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/api/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data.url;
    } catch (error) {
      if (error.message && error.message.includes('compression')) {
        showToast.error('Error compressing image: ' + error.message);
      } else {
        showToast.error('Error uploading image: ' + (error.response?.data?.error || 'Unknown error'));
      }
      return null;
    }
  };

  const handleImageSelect = async (e, isEdit = false) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (isEdit) {
      setUploadingImages(true);
    } else {
      setUploadingImages(true);
    }

    try {
      const uploadPromises = files.map(file => handleImageUpload(file));
      const uploadedUrls = await Promise.all(uploadPromises);
      const validUrls = uploadedUrls.filter(url => url !== null);

      if (isEdit) {
        setEditReportImages(prev => [...prev, ...validUrls]);
      } else {
        setReportImages(prev => [...prev, ...validUrls]);
      }
    } catch (error) {
      showToast.error('Error uploading images');
    } finally {
      setUploadingImages(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleRemoveImage = (index, isEdit = false) => {
    if (isEdit) {
      setEditReportImages(prev => prev.filter((_, i) => i !== index));
    } else {
      setReportImages(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleCreateReport = async (e) => {
    e.preventDefault();
    
    const reportDate = newReport.report_date || new Date().toISOString().split('T')[0];
    const sanitizedContent = sanitizeHTML(newReport.content);

    try {
      await axios.post(`${API_BASE_URL}/api/reports`, {
        report_date: reportDate,
        content: sanitizedContent,
        image_urls: reportImages
      });
      
      showToast.success('Report created successfully!');
      setNewReport({ report_date: '', content: '' });
      setReportImages([]);
      setShowForm(false);
      fetchReports();
    } catch (error) {
      showToast.error('Error creating report: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/reports/${reportId}`);
      showToast.success('Report deleted successfully');
      fetchReports();
      if (expandedReport === reportId) {
        setExpandedReport(null);
      }
      // Remove from reportDetails
      setReportDetails(prev => {
        const updated = { ...prev };
        delete updated[reportId];
        return updated;
      });
    } catch (error) {
      showToast.error('Error deleting report');
    }
  };

  const handleToggleExpand = async (reportId) => {
    if (expandedReport === reportId) {
      setExpandedReport(null);
    } else {
      setExpandedReport(reportId);
      if (!reportDetails[reportId]) {
        await fetchReportDetails(reportId);
      }
    }
  };

  const handleAddComment = async (reportId) => {
    const comment = commentTexts[reportId] || '';
    if (!comment.trim()) return;

    try {
      await axios.post(`${API_BASE_URL}/api/reports/${reportId}/comments`, {
        comment: sanitizeHTML(comment)
      });
      
      setCommentTexts(prev => ({ ...prev, [reportId]: '' }));
      showToast.success('Comment added successfully');
      // Refresh report details to get updated comments
      await fetchReportDetails(reportId);
    } catch (error) {
      showToast.error('Error adding comment: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleDeleteComment = async (reportId, commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/reports/${reportId}/comments/${commentId}`);
      showToast.success('Comment deleted successfully');
      // Refresh report details
      await fetchReportDetails(reportId);
    } catch (error) {
      showToast.error('Error deleting comment');
    }
  };

  const handleEditReport = (report) => {
    setEditingReportId(report.id);
    const reportData = reportDetails[report.id] || report;
    setEditForm({
      report_date: report.report_date,
      content: report.content || reportData?.content || ''
    });
    // Initialize edit images with existing images
    setEditReportImages(reportData?.image_urls || report.image_urls || []);
  };

  const handleCancelEdit = () => {
    setEditingReportId(null);
    setEditForm({ report_date: '', content: '' });
    setEditReportImages([]);
  };

  const handleSaveEdit = async (reportId) => {
    if (!editForm.report_date || !editForm.content.trim()) {
      showToast.warning('Please fill in all fields');
      return;
    }

    try {
      await axios.put(`${API_BASE_URL}/api/reports/${reportId}`, {
        report_date: editForm.report_date,
        content: sanitizeHTML(editForm.content),
        image_urls: editReportImages
      });
      
      showToast.success('Report updated successfully!');
      setEditingReportId(null);
      setEditForm({ report_date: '', content: '' });
      setEditReportImages([]);
      // Refresh reports and report details
      await fetchReports();
      await fetchReportDetails(reportId);
    } catch (error) {
      showToast.error('Error updating report: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const currentReport = expandedReport ? reportDetails[expandedReport] : null;
  const currentComments = currentReport?.comments || [];

  return (
    <div className="izvestaji-container">
      <div className="container">
        <div className="card">
          <div className="izvestaji-header">
            <h2>Reports</h2>
            {!isAdmin && (
              <button onClick={() => setShowForm(!showForm)} className="btn-primary">
                {showForm ? 'Cancel' : 'Create New Report'}
              </button>
            )}
          </div>

          {/* Customer filter for admin */}
          {isAdmin && (
            <div className="customer-filter">
              <label><strong>Filter by Customer:</strong></label>
              <select
                value={selectedCustomer?.id || ''}
                onChange={(e) => {
                  const customer = customers.find(c => c.id === parseInt(e.target.value));
                  setSelectedCustomer(customer || null);
                  setExpandedReport(null);
                  setReportDetails({});
                }}
                className="customer-select-input"
              >
                <option value="">All Customers</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.username}</option>
                ))}
              </select>
            </div>
          )}

          {/* Create Report Form (Customer only) */}
          {!isAdmin && showForm && targetUserId && (
            <div className="create-report-form">
              <h3>Create New Report</h3>
              <form onSubmit={handleCreateReport}>
                <div className="form-group">
                  <label>Report Date:</label>
                  <input
                    type="date"
                    value={newReport.report_date}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      setNewReport({ ...newReport, report_date: selectedDate });
                      // Check if a report already exists for this date
                      const existingReport = reports.find(r => {
                        const reportDate = r.report_date ? r.report_date.split('T')[0] : r.report_date;
                        return reportDate === selectedDate;
                      });
                      if (existingReport) {
                        showToast.info('A report already exists for this date. You can edit it from the reports list.');
                        // Optionally expand the existing report
                        setExpandedReport(existingReport.id);
                      }
                    }}
                    max={new Date().toISOString().split('T')[0]}
                    required
                    style={{
                      backgroundColor: hasReportForDate ? '#e8f5e9' : 'white',
                      borderColor: hasReportForDate ? '#4caf50' : '#ddd',
                      borderWidth: hasReportForDate ? '2px' : '2px',
                      transition: 'all 0.3s ease'
                    }}
                  />
                  {hasReportForDate && (
                    <small style={{ color: '#4caf50', display: 'block', marginTop: '5px', fontWeight: 'bold' }}>
                      ✓ Report exists for this date
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Report Content:</label>
                  <textarea
                    value={newReport.content}
                    onChange={(e) => setNewReport({ ...newReport, content: e.target.value })}
                    placeholder="Write your weekly report here..."
                    rows={10}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Upload Images (optional):</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleImageSelect(e, false)}
                    disabled={uploadingImages}
                    className="image-upload-input"
                  />
                  <small className="upload-hint">Images will be automatically compressed to save bandwidth. Max 10MB per image.</small>
                  {uploadingImages && <p className="upload-status">Compressing and uploading images...</p>}
                  {reportImages.length > 0 && (
                    <div className="image-preview-container">
                      {reportImages.map((url, index) => (
                        <div key={index} className="image-preview-item">
                          <img src={url} alt={`Preview ${index + 1}`} />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(index, false)}
                            className="remove-image-btn"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button type="submit" className="btn-primary" disabled={uploadingImages}>
                  {uploadingImages ? 'Uploading...' : 'Submit Report'}
                </button>
              </form>
            </div>
          )}

          {/* Gym Calendar - Progress Check-In */}
          {targetUserId && (
            <GymCalendar targetUserId={targetUserId} reportDates={reports} />
          )}

          {/* Reports List */}
          {loading ? (
            <div>
              <ReportSkeleton />
              <ReportSkeleton />
            </div>
          ) : reports.length === 0 ? (
            <EmptyState
              icon="📋"
              title="No reports found"
              message={isAdmin ? 'No reports found for selected customer.' : 'You haven\'t created any reports yet. Click "Create New Report" to get started.'}
            />
          ) : (
            <div className="reports-list">
              {reports.map((report) => {
                const reportData = expandedReport === report.id ? reportDetails[report.id] : report;
                const comments = reportData?.comments || [];
                
                return (
                  <div key={report.id} className="report-card">
                    <div className="report-header" onClick={() => handleToggleExpand(report.id)}>
                      <div className="report-title">
                        <h3>
                          {isAdmin && report.username && `${report.username} - `}
                          {formatDate(report.report_date)}
                        </h3>
                        <span className="report-date">Created: {formatDate(report.created_at)}</span>
                      </div>
                      <button className="expand-btn" onClick={(e) => { e.stopPropagation(); handleToggleExpand(report.id); }}>
                        {expandedReport === report.id ? '▼' : '▶'}
                      </button>
                    </div>

                    {expandedReport === report.id && reportData && (
                      <div className="report-details">
                        <div className="report-content">
                          {editingReportId === report.id ? (
                            <div className="edit-report-form">
                              <div className="form-group">
                                <label>Report Date:</label>
                                <input
                                  type="date"
                                  value={editForm.report_date}
                                  onChange={(e) => setEditForm({ ...editForm, report_date: e.target.value })}
                                  max={new Date().toISOString().split('T')[0]}
                                  required
                                />
                              </div>
                              <div className="form-group">
                                <label>Report Content:</label>
                                <textarea
                                  value={editForm.content}
                                  onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                                  placeholder="Write your weekly report here..."
                                  rows={10}
                                  required
                                />
                              </div>
                              <div className="form-group">
                                <label>Images:</label>
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  onChange={(e) => handleImageSelect(e, true)}
                                  disabled={uploadingImages}
                                  className="image-upload-input"
                                />
                                <small className="upload-hint">Images will be automatically compressed to save bandwidth. Max 10MB per image.</small>
                                {uploadingImages && <p className="upload-status">Compressing and uploading images...</p>}
                                {editReportImages.length > 0 && (
                                  <div className="image-preview-container">
                                    {editReportImages.map((url, index) => (
                                      <div key={index} className="image-preview-item">
                                        <img src={url} alt={`Preview ${index + 1}`} />
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveImage(index, true)}
                                          className="remove-image-btn"
                                        >
                                          ×
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="edit-actions">
                                <button
                                  onClick={() => handleSaveEdit(report.id)}
                                  className="btn-primary"
                                  disabled={uploadingImages}
                                >
                                  {uploadingImages ? 'Uploading...' : 'Save Changes'}
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="btn-secondary"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p>{reportData.content || report.content}</p>
                              {(reportData.image_urls || report.image_urls || []).length > 0 && (
                                <div className="report-images">
                                  {(reportData.image_urls || report.image_urls || []).map((url, index) => (
                                    <div 
                                      key={index} 
                                      className="report-image-item"
                                      onClick={() => setSelectedImage(url)}
                                    >
                                      <img src={url} alt={`Report image ${index + 1}`} />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* Comments Section */}
                        <div className="comments-section">
                          <h4>Coach Comments</h4>
                          
                          {/* Display existing comments */}
                          {comments.length > 0 ? (
                            <div className="comments-list">
                              {comments.map((comment) => (
                                <div key={comment.id} className="comment-item">
                                  <div className="comment-header">
                                    <strong>{comment.username}</strong>
                                    <span className="comment-date">{formatDate(comment.created_at)}</span>
                                    {isAdmin && (
                                      <button
                                        onClick={() => handleDeleteComment(report.id, comment.id)}
                                        className="delete-comment-btn"
                                      >
                                        Delete
                                      </button>
                                    )}
                                  </div>
                                  <p>{comment.comment}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="no-comments">No comments yet.</p>
                          )}

                          {/* Add comment form (Admin only) */}
                          {isAdmin && (
                            <div className="add-comment-form">
                              <textarea
                                value={commentTexts[report.id] || ''}
                                onChange={(e) => setCommentTexts(prev => ({ ...prev, [report.id]: e.target.value }))}
                                placeholder="Write a comment..."
                                rows={3}
                              />
                              <button
                                onClick={() => handleAddComment(report.id)}
                                className="btn-secondary"
                              >
                                Add Comment
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Action buttons */}
                        <div className="report-actions">
                          {!isAdmin && report.user_id === user?.id && editingReportId !== report.id && (
                            <button
                              onClick={() => handleEditReport(report)}
                              className="btn-primary"
                              style={{ marginRight: '10px' }}
                            >
                              Edit Report
                            </button>
                          )}
                          {(isAdmin || report.user_id === user?.id) && (
                            <button
                              onClick={() => handleDeleteReport(report.id)}
                              className="btn-danger"
                            >
                              Delete Report
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="image-modal" 
          onClick={() => setSelectedImage(null)}
        >
          <img 
            src={selectedImage} 
            alt="Full size" 
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export default Izvestaji;